import * as React from "react";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { SEARCH_TYPE_TO_LABEL, SearchTypes } from "@/constants";
import DatePicker from "@components/ui/date-picker";
import { Button } from "@components/ui/button";
import { PlusIcon } from "lucide-react";
import { addMonths, endOfDay, isBefore, startOfDay } from "date-fns";
import { Trans, useLingui } from "@lingui/react/macro";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { i18n } from "@lingui/core";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { QueryBuilderForm } from "../types";

type GeneralProps = {
  control: Control<QueryBuilderForm>;
  setValue: UseFormSetValue<QueryBuilderForm>;
  startDate: Date;
  endDate?: Date;
  industries: string[];
};

export default function General({
  control,
  setValue,
  startDate,
  endDate,
  industries,
}: GeneralProps) {
  const { t } = useLingui();

  function handleAddEndDate() {
    const today = new Date();
    const newEndDate = endOfDay(addMonths(startDate || today, 1));
    setValue("endDate", newEndDate);
  }

  function handleChangeStartDate(date?: Date) {
    if (!date) {
      return;
    }

    setValue("startDate", startOfDay(date));
  }

  function handleChangeEndDate(date?: Date) {
    if (date) {
      setValue("endDate", endOfDay(date));
    } else {
      setValue("endDate", undefined);
    }
  }

  function handleDisableEndDate(date: Date) {
    if (startDate) {
      return isBefore(date, startOfDay(startDate));
    }

    return false;
  }

  function handleChangeType(type: SearchTypes) {
    setValue("type", type);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Basinformation</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-title";

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Titel</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      placeholder={t`Ge din bevakning en tydlig och beskrivande titel.`}
                      id={id}
                      aria-invalid={fieldState.invalid}
                      autoComplete="off"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-type";

                return (
                  <Field>
                    <FieldLabel htmlFor={id}>
                      <Trans>Söktyp</Trans>
                    </FieldLabel>
                    <Select
                      value={field.value}
                      onValueChange={(newType) => {
                        handleChangeType(newType as SearchTypes);
                      }}
                    >
                      <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                        <SelectValue placeholder={t`Välj vad du bevakar`} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SEARCH_TYPE_TO_LABEL).map(
                          ([value, label]) => (
                            <SelectItem
                              id={`form-qb-type-${value}`}
                              key={value}
                              value={value}
                            >
                              {i18n._(label)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-start-date";

                return (
                  <Field>
                    <FieldLabel htmlFor={id}>
                      <Trans>Startdatum</Trans>
                    </FieldLabel>
                    <DatePicker
                      value={field.value}
                      id={id}
                      aria-invalid={fieldState.invalid}
                      onChangeDate={handleChangeStartDate}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            {endDate ? (
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState }) => {
                  const id = "form-qb-end-date";

                  return (
                    <Field>
                      <FieldLabel htmlFor={id}>
                        <Trans>Slutdatum</Trans>
                      </FieldLabel>
                      <DatePicker
                        id={id}
                        aria-invalid={fieldState.invalid}
                        isClearable
                        disabled={handleDisableEndDate}
                        clearLabel={t`Ta bort slutdatum`}
                        value={field.value}
                        onChangeDate={(newEndDate) => {
                          handleChangeEndDate(newEndDate);
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            ) : (
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="link"
                  className="-mx-3"
                  onClick={handleAddEndDate}
                >
                  <PlusIcon />
                  <Trans>Lägg till slutdatum</Trans>
                </Button>
              </div>
            )}

            <AccessControl level={ACCESS_LEVELS.STAFF}>
              <Controller
                name="industry"
                control={control}
                render={({ field, fieldState }) => {
                  const id = "form-qb-industry";

                  return (
                    <Field>
                      <FieldLabel staff htmlFor={id}>
                        <Trans>Branschindex (använd ej)</Trans>
                      </FieldLabel>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={(value) => {
                          if (value === "none") {
                            field.onChange(null);
                          } else {
                            field.onChange(value);
                          }
                        }}
                      >
                        <SelectTrigger
                          id={id}
                          aria-invalid={fieldState.invalid}
                        >
                          <SelectValue placeholder={t`Inget`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <Trans>Inget</Trans>
                          </SelectItem>
                          {industries.map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            </AccessControl>
          </div>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
